// types.ts — wire shapes shared by the catalog-list bundle.
// Browser card projections consume the raw character-definition list envelopes.

import type { ThumbnailData } from '../thumbnail/core';
import type { SourceHeaderPicture, SourceHeaderVideo } from '../source-media/core';
import type { FromBaseRow } from '../janus-engine/compile/from-base-context';

export const SAVED_BASE_DEFINITION_LIST_SCHEMA = 'base-definition-list-v2';
export function isBaseDefinitionListSchema(schema: unknown): schema is BaseDefinitionListResponse['schema'] {
    return schema === SAVED_BASE_DEFINITION_LIST_SCHEMA;
}
interface BaseDefinitionListCommon {
    family: 'spells' | 'abilities';
    item_type: string;
    definition_ids: string[];
    base: FromBaseRow;
    total: number;
    pl_persist?: boolean;
}
export type BaseDefinitionListResponse = BaseDefinitionListCommon & {
    schema: typeof SAVED_BASE_DEFINITION_LIST_SCHEMA; stored_pl?: Record<string, number>;
};

export interface CatalogCardSource {
    source_id?: string | null;
    source_name?: string;
    source_abbreviation?: string;
}

export interface CatalogCard {
    item_id: string;
    item_name: string;
    item_url: string;
    picture_url: string | null;
    video_url: string | null;
    video_mime: string | null;
    source: CatalogCardSource | null;
    // Optional per-type numeric attributes baked into the payload so the client can filter on
    // them with no server round-trip (ancestries: ancestral_body/mind/soul/fortune). null = the
    // ancestry has no value for that essence.
    essences?: Record<string, number | null>;
    // Leading icon beside the name. Two forms: a UUID/slug in `icon_id` that the renderer resolves
    // client-side via iconMap() (preferred — keeps the payload pure data, full JS/TS), or legacy
    // pre-rendered `icon_html` (icon_map() output) still honoured for endpoints not yet migrated.
    // Some catalogs show an icon (classes/subclasses); ancestries/lineages don't.
    icon_id?: string | null;
    icon_html?: string | null;
    // Corner-badge icon (subclasses: the required-class icon). Same two forms as the leading icon:
    // `badge_id` (UUID → iconMap() client-side) preferred, legacy `badge_html` still honoured.
    badge_id?: string | null;
    badge_html?: string | null;
    // Optional UUID baked in so a multi-select filter can match client-side (subclasses:
    // the required class). null = no value (excluded when that filter is active).
    requires_class?: string | null;
}

// One arbitrary section in the generalized N-group form of the cards renderer. The fixed
// standard/advanced split can only express two buckets; when a page needs many (e.g. subclasses
// grouped by their required class), the endpoint emits `groups` instead and each entry becomes an
// accent-headed section (icon + label, reusing the rules-index header shape) over its card grid.
export interface CatalogCardGroup {
    label: string;            // header text ('' → no header: an ungrouped run of cards)
    icon_id?: string | null;  // optional header icon UUID/slug, resolved client-side via iconMap()
    accent?: string | null;   // CSS colour for the header's accent bar (--ft-accent); defaults cyan
    cards: CatalogCard[];
}

export interface CatalogResponse {
    sections: {
        standard: CatalogCard[];
        advanced: CatalogCard[];
    };
    // Optional N-group form: when present and non-empty, the cards renderer builds one accent-headed
    // section per entry (e.g. subclasses by class) instead of the fixed standard/advanced split.
    // Keyword + filter-panel filtering still runs across every group.
    groups?: CatalogCardGroup[];
    total: number;
    // Partial payload: the endpoint's compile budget skipped rows (see CatalogTableResponse.incomplete).
    incomplete?: { unchecked: number; candidates: number };
}

// ────────────────────────────────────────────────────────────────────────────
// Table renderer ("catalog-table") wire shapes. Same JSON pipeline as the cards
// (PHP compiles a flat payload from the Redis-cached item arrs), but the mount
// renders a sortable/filterable <table> instead of a card grid. The table is
// fully described by its JSON: the endpoint emits both the column spec and the
// rows, so render-table.ts stays generic across item types.
// Mirrors options/api/backgrounds_list_inc.php.
// ────────────────────────────────────────────────────────────────────────────

// ── Cell/header segments (the pure-data render path) ─────────────────────────────────────────────
// A cell or header is described as an ORDERED LIST OF SEGMENTS instead of a pre-rendered HTML blob,
// so the browser renders icons via the front-end iconMap() (core-helpers.js, reads window.ICON_LOOKUP)
// and never receives PHP icon_map() output. This keeps the endpoints pure data — no icon_map(),
// no get_item_id_name(): the name a `text` segment shows is read by PHP from the item's data shape
// (a nested item_arr shell), not resolved from a bare id at render time.
//   icon  — id is an icon key/slug or UUID. iconMap(id) resolves named icons + NamedIcon UUIDs; if it
//           falls back to help-circle (a catalog-item UUID with its own art, not in ICON_LOOKUP) the
//           renderer uses `pic` (the data-shape pic_url) when present, else iconMap(item_type).
//   text  — escaped text node; optional `link` wraps it in an <a> (item.php?id=…).
//   badge — a small Bootstrap badge (e.g. the ½ half-damage marker).
//   br    — a line break (e.g. AOE type over distance).
//   raw   — trusted-HTML escape hatch for the rare bespoke fragment a segment can't express.
//   thumbnail — a picture/initials thumbnail the renderer builds from arr.pictures DATA (thumbnail.ts:
//           selectThumbnailUrl + buildThumbnailNode). Replaces shipping get_thumbnail() HTML via raw.
export interface IconSegment {
    kind: 'icon';
    id: string;             // icon key/slug or UUID → iconMap()
    item_type?: string;     // fallback icon when `id` resolves to help-circle (catalog-item UUIDs)
    pic?: string;           // pic_url for catalog art not in ICON_LOOKUP (read from the data shape)
    w?: number;
    h?: number;
    title?: string | false; // tooltip override; false → no tooltip wrapper
    link?: string;          // wrap the icon in an <a href>
    target?: string;        // link target (e.g. "_blank")
    cls?: string;           // extra classes on the icon holder (e.g. "me-2" spacing)
    badge?: string;         // small success-pill badge over the icon (e.g. requires_class level)
    // Whitelisted click ACTION: render the icon inside a <button> that fires a known global handler
    // on click (instead of an <a> link). Keeps the payload pure data — the endpoint ships the action
    // name + its argument, never an inline onclick string. Currently:
    //   'audio' → window.playAudioID(actionArg)  — location-type encounter-ambience buttons
    //   'roll'  → window.useAbility(actionArg)    — random-table "roll" buttons
    //   'copy'  → copy actionArg verbatim to the clipboard — UUID copy buttons (enchantment templates)
    // `variant` is the Bootstrap button colour suffix (validated against a whitelist in the renderer).
    action?: 'audio' | 'roll' | 'copy';
    actionArg?: string;     // the id passed to the action handler (audio id / item id / text to copy)
    variant?: string;       // button colour suffix (e.g. 'danger', 'primary'); defaults to 'secondary'
}
// `tip` = instant Bootstrap hover label (data-bs-original-title, NOT a slow native title=), the same
// mechanism the icon segment's `title` uses. Added 2026-08-15 for the PL column: the badge shows the
// ft_pl_pretty() abbreviation ("2.28k"), so the exact figure needs somewhere to live.
export interface TextSegment { kind: 'text'; value: string; link?: string; target?: string; cls?: string; tip?: string; }
export interface BadgeSegment { kind: 'badge'; value: string; variant?: string; cls?: string; tip?: string; }
export interface BreakSegment { kind: 'br'; }
export interface RawSegment { kind: 'raw'; html: string; }
// Rich Quill text — ships RAW text; renderer runs the shared parseText() port (retires PHP parse_text()).
export interface ParseTextSegment { kind: 'parsetext'; text: string; }
// Pure-data thumbnail segment — carries arr.pictures (+ id/name/display opts); thumbnail.ts paints it.
export interface ThumbnailSegment extends ThumbnailData { kind: 'thumbnail'; }
// Browser-built control (never on the wire): a TS adopter such as encounter-list-rows.ts builds the DOM itself so its
// buttons keep real listeners instead of inline onclick strings. `build` runs once per row (render-table memoizes rows).
export interface NodeSegment { kind: 'node'; build: () => Node; }
export type CellSegment = IconSegment | TextSegment | BadgeSegment | BreakSegment | RawSegment | ParseTextSegment | ThumbnailSegment | NodeSegment;

export interface TableColumn {
    key: string;            // matches a key in each row's `cells` map
    header_segments?: CellSegment[]; // pure-data header (preferred — icons via iconMap client-side)
    header_html?: string;   // legacy pre-rendered header inner HTML (honoured for un-migrated endpoints)
    header_label: string;   // plain-text label for aria-label / title
    sortable: boolean;      // clickable header that reorders rows
    filterable: boolean;    // renders a per-column filter <input> in the filter row
    sort_type: 'text' | 'num';
    align?: 'left' | 'center' | 'right';
    width?: string;         // optional fixed column width, e.g. "100px"
}

export interface TableCell {
    segments?: CellSegment[]; // pure-data cell (preferred — rendered client-side via iconMap)
    html?: string;          // legacy pre-rendered cell inner HTML (honoured for un-migrated endpoints)
    sort: string | number;  // sort key for this cell's column (real underlying value, never scraped from the HTML)
    text: string;           // lowercased searchable text for keyword + per-column filtering
    // PLT with rows still awaiting a browser PL (ft_encounter_plt_catalog_cell()): the cell renders the ft-pl-pending
    // treatment and render-table.ts prices `pending_ids` in the browser (shared/pl-browser-pricing.ts), replacing the cell
    // with the number once every row has one. `stored` is the partial stored sum — an input, never shown as the total.
    pl?: { stored: number; pending_ids: string[]; pending_other: number };
}

export interface TableRow {
    item_id: string;
    cells: Record<string, TableCell>;
    // Optional raw filter values for an external filter panel (spells/mentas: spell_level,
    // spell_sphere[], spell_circle, casting time, component/ritual/concentration booleans as "1",
    // roll_type, damage_type, requires_class[]/requires_subclass[], source id). The mount's
    // data-ft-filter-fields maps each panel control's element id → the key here. Single-value
    // controls match by membership; checkboxes require the row's value to be "1". Booleans are
    // kept as "0"/"1" strings (never dropped) so a meaningful falsy like spell level 0 survives.
    filters?: Record<string, string | string[]>;
}

export interface CatalogTableResponse {
    columns: TableColumn[];
    rows: TableRow[];
    total: number;
    // Set when the endpoint's compile budget ran out (options/api/creatures_list_inc.php): `unchecked`
    // of `candidates` items could not be compiled in time, so `rows` is partial. Never cached.
    incomplete?: { unchecked: number; candidates: number };
    // A signed-in viewer: the browser PL pricing of pending `pl` cells also posts the heal claims (ftC2SweepBatch).
    pl_persist?: boolean;
    definition_refusals?: { item_id: string; name: string; reasons: string[] }[];
    definition_schema?: BaseDefinitionListResponse['schema'];
}

// ────────────────────────────────────────────────────────────────────────────
// Rules-index ("catalog-rules") wire shapes. Same JSON pipeline as the cards
// (PHP compiles a flat payload → the TS bundle renders it client-side), but the
// page is a NAVIGATION INDEX, not a filterable catalog of one item_type: many
// labelled groups (Glossary, Player Character creation, Skills, Magic, …), each
// an accent-coloured header over a grid of cards that link to static rule .php
// pages (not item.php?id=). The 2-section standard/advanced model can't express
// ~10 groups, so this is its own renderer (render-rules.ts).
// Mirrors options/api/rules_list_inc.php.
// ────────────────────────────────────────────────────────────────────────────

export interface RuleCard {
    name: string;            // display name (ucwords_and_space of the slug)
    url: string;             // absolute link to the card's .php page ('' when the card uses `action` instead of navigating)
    icon_id?: string | null; // leading icon key/slug → iconMap() client-side (preferred — pure data)
    icon_html?: string;      // legacy pre-rendered leading icon (icon_map() output); honoured for un-migrated endpoints; '' for none
    picture_url: string;     // CDN image for the card face
    text: string;            // lowercased searchable string (the name)
    // Optional Bootstrap button colour suffix for the overlay button (e.g. 'warning' for the
    // player-tools "Random" card). Defaults to 'info'; validated against a whitelist in the
    // renderer so a bad value can't inject an arbitrary class.
    variant?: string;
    // Optional non-navigating action: instead of an <a href> link, the card fires a known
    // client-side handler on click. Whitelisted in the renderer. Currently: 'side_log' →
    // window.openSideLog() (player-tools "Roll Dice" card). When set, `url` is ignored.
    action?: string;
}

export interface RuleGroup {
    key: string;             // stable group key
    label: string;           // header text; '' → no header (continuation group, just a divider)
    accent: string;          // CSS colour for the header's left bar / icon backing (e.g. "#22d3ee")
    icon_id?: string | null; // header icon key/slug → iconMap() client-side (preferred — pure data)
    icon_html?: string;      // legacy pre-rendered header icon (icon_map() output); '' when label is ''
    cards: RuleCard[];
}

export interface CatalogRulesResponse {
    groups: RuleGroup[];
    total: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Game-listing ("catalog-games") wire shapes. Same JSON pipeline as the cards
// (PHP compiles a flat payload → the TS bundle holds it in memory and filters
// client-side), but the card is the bespoke campaign-listing card (header image
// + level/type/mic/webcam/price badges + next session + seats), not the generic
// catalog card. Drives free_games.php / paid_games.php.
// Mirrors options/api/games_list_inc.php.
// ────────────────────────────────────────────────────────────────────────────

export interface GameCard {
    item_id: string;
    name: string;
    url: string;                  // campaign.php?campaign_id=…
    picture_url: string;          // uploaded header or a random default
    level: string;                // campaign_level (display only)
    level_num: number;            // numeric campaign_level — drives the level-range filter
    type_label: string;           // ucwords_and_space(campaign_type)
    type_badge_class: string;     // server-built badge class suffix ("bg-secondary" | "bg-success" | "bg-warning text-dark")
    mic_required: boolean;
    webcam_required: boolean;
    next_session_num: string;     // "next session #" badge (past sessions + 1)
    next_session_date: string;    // formatted in the viewer's timezone, or "None"
    seats_filled: string;
    seats_max: string;
    price: number;                // numeric session price — drives the min/max price filter (paid games)
    price_f: string;              // "$x.xx" for paid games; "" for free (no badge)
    text: string;                 // lowercased searchable string (the name)
}

export interface CatalogGamesResponse {
    // Trusted icon_map() HTML reused across cards (calendar always; mic/webcam per-card). Baked
    // server-side so the renderer never re-implements icon_map() in TS (same contract as the rules cards).
    icons: { calendar: string; microphone_required: string; webcam_required: string };
    games: GameCard[];
    total: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Adventure-listing ("catalog-adventures") wire shapes. Same JSON pipeline as the
// cards (PHP compiles a flat payload from fetch_adventures() → the TS bundle holds
// it in memory and filters by keyword client-side), but the card is the bespoke
// adventure catalog card (2/3 cover image or hover-play header video + level-range
// badges + optional credit + price/release footer). Drives adventures.php /
// free_adventures.php / paid_adventures.php.
// Mirrors options/api/adventures_list_inc.php.
// ────────────────────────────────────────────────────────────────────────────

export interface AdventureCard {
    item_id: string;
    name: string;
    url: string;                  // adventure.php?adventure_id=…
    picture_url: string;          // uploaded header cover or the worlds.jpg default
    video_url: string;            // header clip URL; '' when the adventure has no header video
    poster_url: string;           // cover used as the video poster; '' unless a video is shown
    level_begin: string;          // adventure_level_begin (display badge)
    level_end: string;            // adventure_level_end (display badge)
    credit_label: string;         // source name, else publisher creator username; '' to hide the line
    is_free: boolean;             // price <= 0
    price: number;                // numeric price — drives the min/max price filter (paid adventures)
    price_f: string;              // "x.xx" for paid (rendered as "$x.xx"); "" for free
    release_date: string;         // "Mon YYYY" from adventure_t; '' to hide
    parent_name?: string;         // nested adventures: public parent bundle's name; '' / absent to hide
    parent_url?: string;          // adventure.php?adventure_id=<parent>; '' when parent_name hidden
    text: string;                 // lowercased searchable string (name + public parent name)
}

// adventure-list-v1 wire rows (projected by adventure-list-data.ts). Kept here, types-only, so importers of
// types.ts never pull in the catalog-list runtime.
type AdventureNumeric = number | string | null;
export interface AdventurePictureRow {
    picture_i: AdventureNumeric;
    picture_id: string;
    picture_extension: string | null;
    picture_private: AdventureNumeric;
    picture_created_by_id: string | null;
    picture_attached_item_type: string;
    picture_reference_attached_item_type: string | null;
    primary_picture: AdventureNumeric;
    picture_thumbnail: AdventureNumeric;
    picture_number: AdventureNumeric;
}
export interface AdventureVideoRow {
    video_i: AdventureNumeric;
    video_id: string;
    video_file_extension: string | null;
    video_private: AdventureNumeric;
    video_created_by_id: string | null;
    video_attached_type: string;
    video_primary: AdventureNumeric;
    video_order: AdventureNumeric;
}
export interface AdventureListRow {
    adventure_id: string;
    adventure_name: string | null;
    adventure_t: string | null;
    adventure_private: AdventureNumeric;
    adventure_deleted: AdventureNumeric;
    adventure_created_by_id: string | null;
    adventure_level_begin: AdventureNumeric;
    adventure_level_end: AdventureNumeric;
    adventure_price: AdventureNumeric;
    adventure_parent_id: string | null;
    adventure_source_name: string | null;
    adventure_creator_username: string | null;
    adventure_creator_al: AdventureNumeric;
    adventure_parent_name: string | null;
    adventure_parent_private: AdventureNumeric;
    adventure_parent_deleted: AdventureNumeric;
    adventure_child_count: AdventureNumeric;
    adventure_header_pictures: string | AdventurePictureRow[] | null;
    adventure_header_videos: string | AdventureVideoRow[] | null;
}
export interface AdventureListResponse {
    schema: 'adventure-list-v1';
    mode: 'my' | 'browse';
    rows: AdventureListRow[];
    total: number;
}

export type CatalogAdventuresResponse = AdventureListResponse;

// ────────────────────────────────────────────────────────────────────────────
// Library ("catalog-library") wire shapes. Same JSON pipeline as the cards (PHP
// compiles a flat payload from fetch_sources() → the TS bundle holds it in memory
// and filters by keyword + product type client-side), but the cards are the source
// (digital product) catalog cards, GROUPED by entitlement (catalog grid, collapsible
// FateTide Plus bundle, purchased/Owned section). The entitlement PARTITION + subscribe
// CTAs are decided server-side; the client only renders. Drives library.php / my_library.php.
// Mirrors options/api/library_sources_list_inc.php.
// ────────────────────────────────────────────────────────────────────────────

export interface LibraryProductType { id: string; name: string; }

// Raw header media shipped per source card — the front-end picks image-vs-video + cover/default
// (source-media/core.ts: resolveSourceMedia / pickSourceHeaderPicUrl), a port of the PHP
// ft_source_header_pic_url() + legacy table media decision. The shapes live in the shared source-media
// core so the catalog-list card renderers and the global source-media mount bundle use one definition;
// re-exported here for existing consumers that import them from './types'.
export type { SourceHeaderPicture, SourceHeaderVideo } from '../source-media/core';

export interface LibrarySourceCard {
    source_id: string;
    name: string;
    href: string;                 // source.php?source_id=…
    pictures: SourceHeaderPicture[]; // header pictures (cover + thumbnails); FE picks the cover
    videos: SourceHeaderVideo[];     // header videos; primary (else first) wins over the cover image
    product_types: LibraryProductType[]; // badges + the ids the chip filter matches against
    is_private: boolean;
    release: string;              // "Mon j, YYYY"; '' to hide
    price_label: string;          // "Free" | "$x.xx" (library) | "Owned" | "Included with Plus" (my_library)
    price_class: string;          // bootstrap text colour for the footer label
    text: string;                 // lowercased searchable string (the name)
}

export interface LibraryGroup {
    key: string;
    style: 'grid' | 'card' | 'details'; // 'grid' = bare grid; 'card' = bundle card; 'details' = collapsible bundle
    card_kind?: 'source' | 'adventure'; // absent/'source' → LibrarySourceCard; 'adventure' → AdventureCard (Purchased Adventures group)
    title: string;                // '' → no header (the bare catalog grid)
    blurb: string;
    border: string;               // bootstrap colour suffix for the bundle border ('info' | 'success' | '')
    open: boolean;                // initial open state for the 'details' bundle
    subscribe: { show: boolean; href: string; label: string } | null;
    cards: LibrarySourceCard[] | AdventureCard[];
}

export interface CatalogLibraryResponse {
    groups: LibraryGroup[];
    product_types: LibraryProductType[]; // full chip set for the product-type filter
    icons: { private: string; level?: string }; // trusted icon_map() HTML (level: adventure-card badges)
    default_cover_base: string;          // CDN dir for the random default-cover fallback
    total: number;
}

// ────────────────────────────────────────────────────────────────────────────
// My-campaigns ("catalog-my-campaigns") wire shapes. Same dashboard-card pattern as
// my-worlds/my-adventures: the endpoint ships structured data and the browser owns
// the card shell + management actions.
// Mirrors options/api/my_campaigns_list_inc.php.
// ────────────────────────────────────────────────────────────────────────────

export interface MyCampaignCard {
    client_campaign?: Record<string, unknown>;
    item_id: string;
    name: string;                 // campaign name (title + alt + search key)
    url: string;                  // my_campaign.php?campaign_id=… (the header image link)
    picture_url: string;          // uploaded campaign header or a random default
    level: string;
    type_label: string;
    type_badge_class: string;
    mic_required: boolean;
    webcam_required: boolean;
    locations_url: string;
    encounters_url: string;
    players_url: string;
    requested_signup: boolean;
    join_requests_url: string;
    pending_join_requests: number;
    invite_code: string;
    invite_link: string;
    public_url: string;
    edit_url: string;
    media_url: string;
    settings_url: string;
    share_settings_url: string;
    is_running: boolean;
    is_private: boolean;
    sessions_url: string;
    next_session_number: string;
    next_session_date: string;
    next_session_time: string;
    seats_filled: number;
    max_players: number;
    world_id: string;
    world_name: string;
    calendar_id: string;
    calendar_name: string;
    current_date: string;
    current_time: string;
    start_date: string;
    end_date: string;
    text: string;                 // lowercased searchable string (the name)
}

export interface CatalogMyCampaignsResponse {
    cards: MyCampaignCard[];
    total: number;
}

// ────────────────────────────────────────────────────────────────────────────
// My-campaign-players ("catalog-my-campaign-players") wire shapes. Drives the
// GM's campaign player table at tools/campaigns/my_campaign_players.php. The
// endpoint ships player/character rows and the browser owns the action handlers.
// ────────────────────────────────────────────────────────────────────────────

export interface MyCampaignPlayerRow {
    thumbnail_inputs?: Record<string, unknown>;
    client_computed?: unknown;
    client_creature?: Record<string, unknown>;
    index: number;
    item_id: string;
    character_name: string;
    character_url: string;
    player_username: string;
    player_url: string;
    level: string;
    thumbnail_html: string;
    class_icons_html: string;
    edit_url: string;
    inactive: boolean;
    text: string;
}

export interface CatalogMyCampaignPlayersResponse {
    players: MyCampaignPlayerRow[];
    total: number;
    invite_code?: string;
}

export interface CampaignSessionPlayerRow extends MyCampaignPlayerRow {
    campaign_id: string;
    campaign_session_id: string;
    skipping: boolean;      // not attending — true for EITHER cancel action
    late_cancel: boolean;   // narrower: cancelled inside the 24h pre-start window
    late: boolean;
    no_show: boolean;
    left_early: boolean;
    can_manage_character: boolean;
    can_manage_attendance: boolean;
    can_kick: boolean;
}

export interface CatalogCampaignSessionPlayersResponse {
    players: CampaignSessionPlayerRow[];
    total: number;
}

export interface MyLocationRow {
    item_id: string;
    name: string;
    url: string;
    edit_url: string;
    copy_url: string;
    map_html: string;
    picture_html: string;
    description_html: string;
    location_type: string;
    location_type_label: string;
    parent_id: string;
    parent_name: string;
    parent_url: string;
    zone_prefix: string;
    is_private: boolean;
    text: string;
}

export interface CatalogMyLocationsResponse {
    mode: 'my' | 'template';
    edit_type: 'campaign' | 'adventure' | '';
    variable_id: string;
    locations: MyLocationRow[];
    total: number;
    /** The viewer's session CSRF token for the Copy POST forms (copy_location_inc.php refuses without it). */
    csrf_token?: string;
}

// ────────────────────────────────────────────────────────────────────────────
// My-worlds ("catalog-my-worlds") wire shapes. Same dashboard-card pattern as
// my-campaigns, but the world management card is small enough to keep pure-data:
// header picture + title + view/edit/add-location/media/private/delete actions
// are rendered fully client-side. Drives my_worlds.php.
// Mirrors options/api/my_worlds_list_inc.php.
// ────────────────────────────────────────────────────────────────────────────

export interface MyWorldCard {
    item_id: string;
    name: string;
    url: string;
    picture_url: string;
    edit_url: string;
    add_location_url: string;
    media_url: string;
    show_private: boolean;
    is_private: boolean;
    text: string;
}

export interface CatalogMyWorldsResponse {
    cards: MyWorldCard[];
    total: number;
}

// Public Worlds browse (worlds.php, options/api/worlds_list_inc.php): every public world, read-only cards.
export interface WorldBrowseCard {
    item_id: string;
    name: string;
    url: string;
    timeline_url: string;
    picture_url: string;
    place_count: number;
    text: string;
}

export interface CatalogWorldsResponse {
    cards: WorldBrowseCard[];
    total: number;
}

// ────────────────────────────────────────────────────────────────────────────
// My-adventures ("catalog-my-adventures") wire shapes. Same JSON pipeline as the
// cards/my-campaigns (PHP compiles a flat payload from fetch_adventures() → the TS
// bundle holds it in memory and filters by name client-side), but the card is the
// rich MY-ADVENTURE management card: a 10:13 cover image or hover-play header video,
// level/price/release footer, AND a dense interactive management cluster (locations/
// encounters, edit/private/delete, media manager). The cover is the view link; the TS renderer owns the card
// shell and action buttons; the PHP endpoint ships pure data only. Drives my_adventures.php.
// Mirrors options/api/my_adventures_list_inc.php.
// ────────────────────────────────────────────────────────────────────────────

export interface MyAdventureCard {
    item_id: string;
    name: string;                 // adventure name (title + search key)
    url: string;                  // adventure.php?adventure_id=...
    picture_url: string;          // uploaded header or catalog fallback
    video_url: string;            // optional primary/fallback header video
    level_begin: string;
    level_end: string;
    is_free: boolean;
    price_f: string;
    release_date: string;
    locations_url: string;
    encounters_url: string;
    edit_url: string;
    media_url: string;
    is_private: boolean;
    child_count?: number;         // direct sub-adventures the viewer owns (0 = leaf)
    children_url?: string;        // drill-down to this master's sub-adventures (empty when child_count 0)
    text: string;                 // lowercased searchable string (the name)
}

export type CatalogMyAdventuresResponse = AdventureListResponse;

// ────────────────────────────────────────────────────────────────────────────
// My-sources ("catalog-my-sources") wire shapes. Same JSON pipeline as the rest
// (PHP compiles a flat payload from fetch_sources() → the TS bundle holds it in
// memory and filters by keyword + product type client-side), but the card is the
// rich SOURCE management card: image/video header, product-type badges, price/
// release footer, AND a dense interactive management cluster (view/edit, media
// manager, private/delete). The TS renderer owns the card shell, source media,
// and action buttons; the PHP endpoint ships pure data only. Each card also
// carries its product_type ids so the chip filter can narrow the grid client-side.
// Drives my_sources.php.
// Mirrors options/api/my_sources_list_inc.php.
// ────────────────────────────────────────────────────────────────────────────

export interface MySourceCard {
    item_id: string;
    name: string;                 // source name (title + search key)
    url: string;                  // source.php?source_id=...
    edit_url: string;
    media_url: string;
    pictures: SourceHeaderPicture[];
    videos: SourceHeaderVideo[];
    is_private: boolean;
    price_label: string;
    price_class: string;
    release: string;
    text: string;                 // lowercased searchable string (the name)
    product_types: string[];      // product-type UUIDs this source carries (chip-filter match set)
    product_type_badges: LibraryProductType[];
    review_status?: string;
    manage_catalog?: boolean;
}

export interface MySourceGroup {
    label: string;                // 'Public' | 'Private' ('' → no heading)
    icon?: string | null;         // leading section-title icon key → iconMap() client-side
    cards: MySourceCard[];
}

export interface CatalogMySourcesResponse {
    groups: MySourceGroup[];
    product_types: LibraryProductType[]; // distinct product types across the viewer's sources (chip set)
    default_cover_base: string;          // CDN dir for the random default-cover fallback
    total: number;
}

// ────────────────────────────────────────────────────────────────────────────
// My-wishlist ("catalog-my-wishlist") wire shapes. The endpoint ships structured
// source-card data and the browser owns the card shell + move/remove actions.
// Drives my_wishlist.php.
// Mirrors options/api/my_wishlist_list_inc.php.
// ────────────────────────────────────────────────────────────────────────────

export interface MyWishlistCard {
    item_id: string;
    name: string;
    url: string;
    pictures: SourceHeaderPicture[];
    videos: SourceHeaderVideo[];
    price_label: string;
    price_class: string;
    type_label?: string;
    product_type_badges: LibraryProductType[];
    text: string;
}

export interface CatalogMyWishlistResponse {
    cards: MyWishlistCard[];
    default_cover_base: string;
    wishlist_count?: number;
    total: number;
}

// ────────────────────────────────────────────────────────────────────────────
// User-profile ("catalog-user-profile") wire shape. NOT a list — a SINGLE public
// profile record (user.php). Same JSON pipeline as the rest (PHP compiles a flat
// payload → the TS bundle paints it client-side), but the payload describes one
// user: identity fields + aggregate player/GM counts (computed via cheap COUNT(*)
// queries, not fetch-and-count) + structured social links, rich-text source, and
// the shared carousel payload for the portrait. render-user-profile.ts owns the
// markup and hydrates icons + parse-text mounts inside.
// Mirrors options/api/user_profile_inc.php.
// ────────────────────────────────────────────────────────────────────────────

export interface UserStat {
    count: number;
    percent: string;              // "8%" or "—"
}

export interface UserSocialLink {
    platform: string;
    url: string;
    label: string;
}

export interface UserProfileMedia {
    domId: string;
    name: string;
    height: number;
    pictures: unknown[];
    videos: unknown[];
    activePicId: string | null;
    setPic: null;
}

export interface UserProfileResponse {
    user_id: string;
    username: string;
    identity: {
        account_type: string;
        years_ttrpg: string;
        years_gm: string;
        timezone_label: string;   // "America/New_York (GMT-5)"
        tz_diff_label: string;    // "Same timezone" | "+3 hours"
    };
    media: UserProfileMedia | null; // null → no picture
    socials: UserSocialLink[];    // structured social-link row
    about_text: string;           // raw parse-text source; '' → section hidden
    gm_style_text: string;        // raw parse-text source; '' → section hidden
    player_stats: {
        games_played: number;
        no_show: UserStat;
        late: UserStat;
        left_early: UserStat;
        skipped: UserStat;          // cancelled with 24h+ notice
        late_cancel: UserStat;      // cancelled inside the 24h pre-start window
        attendance_score: string; // number as string, or "—"
    };
    gm_stats: {
        games_ran: number;
        campaigns_running: number;
        sessions_planned: number;
    };
    error?: string;
}

// Resolved configuration for one mounted catalog list, parsed from data-* attributes.
export interface CatalogListConfig {
    endpoint: string;                       // relative endpoint path (POSTed to)
    renderMode: 'cards' | 'table' | 'rules' | 'games' | 'adventures' | 'library' | 'cosmetics' | 'my-campaigns' | 'my-campaign-players' | 'campaign-session-players' | 'my-locations' | 'my-worlds' | 'worlds' | 'my-adventures' | 'my-sources' | 'my-wishlist' | 'user-profile' | 'world-timeline' | 'world-book' | 'world-book-rail' | 'kin-family' | 'family-tree' | 'articles-tree'; // data-ft-render: 'table' → sortable table, 'rules' → grouped index, 'games' → campaign-listing cards, 'adventures' → adventure catalog cards, 'library' → entitlement-grouped source cards, 'my-campaigns' → GM campaign-management cards, 'my-campaign-players' → GM player table, 'campaign-session-players' → session attendance table, 'my-locations' → location management/template table, 'my-worlds' → my-world management cards, 'worlds' → public world browse cards, 'my-adventures' → my-adventure management cards, 'my-sources' → my-source management cards (grouped + product-type chips), 'my-wishlist' → source wishlist cards, 'user-profile' → single public user profile, 'world-timeline' → a world's chronology (era bands + per-track lanes), 'kin-family' → one creature's immediate family + the authoring widget, 'family-tree' → the full generation-band family diagram, 'articles-tree' → one scope's articles as a folder tree, else card grid (default)
    searchInputId: string | null;           // id of the live keyword <input>
    filterButtonId: string | null;          // id of the "apply filter" button
    filterClearButtonId: string | null;     // id of the "clear all filters" button (resets panel controls + re-filters)
    filterPanelId: string | null;           // id of the collapsible filter panel to close on apply
    groupNavId: string | null;              // id of an external container to fill with per-group jump buttons (N-group form only)
    sectionLabels: { standard: string; advanced: string };
    filterFields: Record<string, string>;   // { paramName: elementId } — values read on every fetch
    loadingLogo: string | null;             // optional brand mark URL for the centered loading state
    loadingText: string | null;             // optional caption under the loader (default "Loading")
    // Table-only render cap (data-ft-table-cap). When set (>0), the table renderer stops eagerly
    // building a <tr> for every row (which hangs/crashes the tab on huge tables like the audio
    // library — thousands of inline <audio> elements + tooltip instances). Instead it builds rows
    // LAZILY for just the current search/filter subset, capped to this many. null/0 → the default
    // eager render (unchanged for every existing catalog table). Filtering stays fully client-side.
    tableCap: number | null;
}

// ────────────────────────────────────────────────────────────────────────────
// World-timeline ("catalog-world-timeline") wire shapes. Same JSON pipeline as
// the cards/my-worlds modes (PHP compiles a flat payload; TypeScript renders),
// but the payload is a CHRONOLOGY rather than a list of cards.
// Mirrors options/api/world_timeline_inc.php. Drives world_timeline.php.
//
// ⭐ `entries` and `eras` arrive ALREADY ORDERED, by the signed sort key that
// makes "300 BT → 0 BT → 432 AT" work (BT counts DOWN — see
// includes/functions/db/timeline_events/ft_timeline_date_inc.php). The renderer
// must never re-sort them: ordering by `start.year` silently reverses the whole
// BT half. Filtering toggles visibility only.
// ────────────────────────────────────────────────────────────────────────────

// One endpoint of an entry. Every part is optional because most of a world's
// history is dated to a bare year — month/day/time are the exception, not the rule.
export interface WorldTimelineDateParts {
    year: number | null;
    month: number | null;
    day: number | null;
    era: string | null;       // 'BT' | 'AT' — normalised server-side; blank means AT
    minute: number | null;    // minute-of-day, for the entries that carry a clock time
    approximate: boolean;     // the "~0 BT" shape
}

export interface WorldTimelineTrack {
    timeline_id: string;
    name: string;
    description: string;
    colour: string;           // '' or #rrggbb (whitelisted server-side, so safe as a CSS value)
    parent_id: string;        // this track nests under that one
    sort_order: number;
    is_private: number;       // 3-value enum: 0 public / 1 private / 2 secret
    campaign_id: string;
    include_location_dates: boolean;   // §6.5 projection toggle — the FLAG, not just its effect
    // Tracks NEST (Stormbrook under Orrin under Zin). Both computed server-side with a hop cap and
    // a cycle guard, so the renderer never walks the parent chain itself.
    depth: number;              // 0 = root; drives the chip indent and the elbow branch
    descendant_ids: string[];   // every track BELOW this one, self excluded
}

export interface WorldTimelineCategory {
    slug: string;
    label: string;
    icon: string;             // a real CDN icon name, resolved client-side through iconMap()
    colour: string;           // #rrggbb — the medallion ring
}

export interface WorldTimelineEntry {
    sampled?: boolean;
    event_id: string;         // a UUID, OR "loc:<uuid>:founding" for a projected location date
    kind: string;             // 'event' | 'era'
    category: string;         // raw, as the author typed it
    // Resolved server-side so PHP and TS cannot drift on which icon a category means.
    category_slug: string;
    category_label: string;
    category_icon: string;
    category_colour: string;
    name: string;
    description: string;      // Quill rich text (HTMLPurifier-sanitised on write)
    gm_notes: string;         // author-only; always '' for a player read
    location_id: string;
    // AGE BANNERS. The column stores a pictures.picture_id; the endpoint resolves it to a URL at
    // read time, scoped to pictures attached to this world — so a stale or crafted id yields ''
    // rather than a window onto somebody else's art.
    banner_picture_id: string;
    banner_url: string;
    is_private: number;
    // ⭐ Canon tier (owner, 2026-08-23): 'bedrock' | 'default' | 'campaign'. The tier is the flag;
    // campaign_name is '' unless the viewer may see inside that campaign (or authors the world).
    canon_tier: string;
    campaign_id: string;
    campaign_name: string;
    derived: boolean;         // true = projected from a location's date columns, read-only here
    source: { location_id: string; date_field: string };
    start: WorldTimelineDateParts;
    end: WorldTimelineDateParts;
    open_start: boolean;      // "… – 300 BT"
    open_end: boolean;        // "411 AT – present"
    start_sort_key: number | null;   // the ordering authority; null = beginningless
    end_sort_key: number | null;
    start_sort_year: number | null;  // signed year (BT negative) — safe for gap arithmetic
    end_sort_year: number | null;
    start_day_abs: number | null;    // calendar-aware absolute day, for proportional spacing
    end_day_abs: number | null;
    label: string;            // ready-made display string, formatted server-side
    timeline_ids: string[];   // §3c: the tracks this entry appears on
    text: string;             // lowercased name, for client-side keyword search
}

// The campaign clock's "you are here" pin, present only when ?campaign_id= named a
// campaign that genuinely belongs to this world.
export interface WorldTimelineMarker {
    campaign_id: string;
    name: string;
    label: string;
    sort_key: number | null;
    sort_year: number | null;
    day_abs: number | null;
    calendar_matches_world: boolean;
}

export interface WorldTimelineCalendarMonth {
    order: number;
    name: string;
    days: number;
}

export interface WorldTimelinePicture {
    picture_id: string;
    url: string;
    thumbnail: string;
    name: string;
    is_private: number;       // choosing one as a banner PUBLISHES it — the picker says so
}

// The World Book paywall state (world_gate_inc.php ft_world_gate_state()). Declared HERE, not in world-book-gate.ts: types.ts is
// type-imported by the janus-engine and websocket programs, and world-book-gate.ts pulls the whole catalog-list app in through
// resolveUrl — a type reference to it broke both programs' typecheck (coordinator v2-2e, 2026-10-19).
export interface WorldBookGate {
    gated: boolean; entitled: boolean; via_plus: boolean; source_name: string; source_url: string;
    subscribe_url: string; login_url: string; sample_words: number;
}

export interface CatalogWorldTimelineResponse {
    gate: WorldBookGate;
    world: {
        location_id: string;
        name: string;
        url: string;
        edit_url: string;
        is_private: number;
    };
    calendar: {
        calendar_id: string;
        months: WorldTimelineCalendarMonth[];
        days_in_year: number;
        has_calendar: boolean;
    };
    categories: WorldTimelineCategory[];   // the closed vocabulary, for the editor's datalist
    can_author: boolean;      // a UI hint only — the endpoints gate every write themselves
    campaigns?: { campaign_id: string; name: string }[];   // the editor's campaign picker; author-only
    tracks: WorldTimelineTrack[];
    eras: WorldTimelineEntry[];
    entries: WorldTimelineEntry[];
    // The Age-banner picker's options. Author-only: empty for players.
    world_pictures?: WorldTimelinePicture[];                          // world art, reusable on any Age
    entry_pictures?: Record<string, WorldTimelinePicture[]>;          // an Age's OWN uploads, by entry id
    media_manager_base?: string;                                      // + entry id = the Upload page
    marker?: WorldTimelineMarker | null;
    total: number;
    error?: string;
}


// ══════════════════════════════════════════════════════════════════════════════════════════════
// Creature family trees — mirrors options/api/kin_inc.php.
// Schema + design decisions: sql/creature_kin_tables.sql.
// ══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * One creature in a family, plus the metadata of the EDGE that reached it.
 *
 * ⭐ The edge fields are what make this a table rather than two item_layer_types: `qualifier`,
 * `disputed` and `note` have nowhere to live on a bare UUID layer value, and the paper fixture
 * (docs/lore/02-pantheon.md) needs all three on day one — it records two different mothers for
 * Ivona and refuses to choose between them.
 *
 * `hidden` means the SERVER withheld the name: a public edge may point at a private creature, and
 * the edge being visible never makes its endpoint visible. `name` is then a placeholder, so a
 * renderer must not treat it as real.
 */
export interface KinNode {
    kind: 'item' | 'encounter_creature' | string;
    id: string;
    item_id: string | null;
    encounter_id: string | null;
    name: string;
    hidden: 0 | 1;
    item_type: string | null;
    /** Primary picture, batch-resolved server-side. Null when the creature has no art, or is hidden. */
    pic_url: string | null;
    /**
     * The node's finished address, resolved SERVER-SIDE. Present on article nodes, which carry a
     * readable slug this bundle cannot derive from an id. Absent on other kinds, which still build
     * their href from item_id/encounter_id below.
     */
    url?: string | null;

    // Edge metadata — null on a bare node (the focus), populated on every related node.
    kin_link_id: string | null;
    relation: 'parent' | 'union' | null;
    role: string | null;            // mother | father | parent | spouse | consort | partner
    qualifier: string | null;       // birth | adoptive | step | foster | unknown
    disputed: 0 | 1;
    note: string | null;
    edge_private: 0 | 1 | 2;        // 0 public / 1 private / 2 secret — the AUTHOR's filing
    inherited: 0 | 1;               // borrowed from the catalog creature (§4e-bis hybrid)

    // Siblings only. `half` is computed over UNDISPUTED edges on BOTH sides — see
    // ft_kin_siblings_of(). Counting disputed edges would make every sibling read as "half".
    half: number | null;
    shared_parents: number | null;

    // Tree walks only: distance from the focus, 1 = my parents / my children.
    generation: number | null;

    editable: 0 | 1;

    /**
     * Birth and death, batch-resolved server-side (ft_kin_attach_lifespans). Null when the creature
     * has no record, or is hidden — a hidden node publishing its birth year would narrow a secret
     * NPC to one generation.
     */
    life: KinLifespan | null;
}

/** The lifespan parts a family card draws. Assembled by ft_kin_lifespan_display(). */
export interface KinLifespan {
    /** Formatted, e.g. "91 BT" or "~5 AT". Null when unrecorded. */
    birth: string | null;
    death: string | null;
    /**
     * ⚠ ROUTINELY NULL AND THAT IS CORRECT. Nothing stores a world's present year yet, so outside a
     * campaign there is no "now" to count from. A death still yields an age-at-death, which needs no
     * basis at all. Never substitute a guess.
     */
    age: number | null;
    age_source: string | null;      // 'at_death' | 'campaign' | … — what the age was measured against
    status: 'alive' | 'dead' | 'unknown' | 'undying' | string;
    is_dead: 0 | 1;
    /** "Always existed in the void of time" — a real state, not a missing year. */
    eternal: 0 | 1;
    inherited: 0 | 1;
    /**
     * ⭐ THE ORDERING AUTHORITY. Signed, so BT (which counts DOWN) sorts before AT. Siblings on a
     * family chart belong in birth order and a raw year would sort the pre-Titan half backwards.
     */
    birth_sort_key: number | null;
}

/** action=family — everything the item-page Family block and the authoring widget draw. */
export interface KinFamilyResponse {
    ok: boolean;
    node: KinNode;
    parents: KinNode[];
    partners: KinNode[];
    children: KinNode[];
    siblings: KinNode[];
    /** Derived server-side from the viewer's permissions — never a request field. */
    can_author: 0 | 1;
    /** A placement still borrowing the catalog creature's family; the first edit copies it down. */
    is_inherited: 0 | 1;
    vocabulary: {
        relations: string[];
        parent_roles: string[];
        union_roles: string[];
        qualifiers: string[];
    };
}

/** One parent edge between two walked nodes. Source of the diagram's lines. */
export interface KinTreeEdge {
    child: { kind: string; id: string };
    parent: KinNode;
}

/** action=tree — the deep walk that family_tree.php lays out into generation bands. */
export interface KinTreeResponse {
    ok: boolean;
    focus: KinNode;
    ancestors: KinNode[];
    descendants: KinNode[];
    edges: KinTreeEdge[];
    partners: Array<{ a: { kind: string; id: string }; b: KinNode }>;
    max_depth: number;
    /** The walk hit the server's node cap; the diagram shows part of the family, not all of it. */
    truncated: 0 | 1;
    can_author: 0 | 1;
}
